import React, { useCallback, useContext, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import { ModelContext } from "contexts/ModelDetailContext";
import {
	getModelDetails,
	getModelLists,
} from "services/models/modelDetails/details";
import PageNotFound from "components/Elements/PageNotFound";
import { COLLAPSEDID } from "constants/modelDetails";
import { appPath, modelsPath } from "helpers/routePaths";

function ModelDetails(props) {
	const [state, dispatch] = useContext(ModelContext);
	const [isLoading, setIsLoading] = useState(true);
	const [isError, setIsError] = useState(false);
	const [translationGate, setTranslationGate] = useState(null);
	const { siteAppID } =
		JSON.parse(sessionStorage.getItem("me")) ||
		JSON.parse(localStorage.getItem("me"));

	const navigate = useNavigate();
	const { id } = useParams();

	const fetchModelDetails = useCallback(async () => {
		const response = await Promise.all([
			getModelDetails(id),
			getModelLists(siteAppID),
		]);
		if (!response?.every((res) => res.status)) {
			setIsError(true);
		} else if (response[0].data?.translationStatus === "InProgress") {
			setTranslationGate({
				language: response[0].data?.translationTargetLanguage,
				country: response[0].data?.translationCountry,
			});
		} else {
			setTranslationGate(null);
			dispatch({
				type: "SET_MODEL_DETAIL",
				payload: {
					detail: response[0].data,
					activeModelVersion:
						response[1].data.find(
							(model) => model?.activeModelVersionID === +id
						)?.activeModelVersion ?? null,
				},
			});
		}
		setIsLoading(false);
	}, [id, dispatch, siteAppID]);
	useEffect(() => {
		fetchModelDetails();
		if (JSON.parse(localStorage.getItem("serviceLayoutData"))?.modelId !== id)
			localStorage.removeItem("serviceLayoutData");

		return () => sessionStorage.removeItem(COLLAPSEDID);
	}, [fetchModelDetails, id]);
	// console.log(isError);

	if (isLoading) {
		return <CircularProgress />;
	}

	if (isError) {
		return <PageNotFound message="Model Not Found" />;
	}

	if (translationGate) {
		return (
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					gap: 2,
					p: 6,
					textAlign: "center",
				}}
			>
				<HourglassTopRoundedIcon
					sx={{ fontSize: 64, color: "#8a6d00" }}
				/>
				<Typography variant="h5" sx={{ fontWeight: 600 }}>
					Translation in progress
				</Typography>
				<Typography variant="body1" sx={{ maxWidth: 480 }}>
					This model is being translated to{" "}
					<strong>
						{translationGate.language}
						{translationGate.country
							? `-${translationGate.country.toUpperCase()}`
							: ""}
					</strong>
					. You can't edit it until translation is complete.
				</Typography>
				<Typography variant="body2" sx={{ color: "#666" }}>
					Refresh this page in a few minutes, or return to the Models list to check status.
				</Typography>
				<Box sx={{ display: "flex", gap: 2, mt: 1 }}>
					<Button
						variant="outlined"
						onClick={() => navigate(appPath + modelsPath)}
					>
						Back to Models
					</Button>
					<Button
						variant="contained"
						onClick={() => fetchModelDetails()}
					>
						Refresh
					</Button>
				</Box>
			</Box>
		);
	}

	return <>{props.children(state.modelDetail)}</>;
}

export default ModelDetails;
